// @ts-strict-ignore
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaVO, MetaElementUsageVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({ providedIn: 'root' })
export class BridgeArticleManager {
  constructor(
    private sceneManager: SceneManager,
    private _articleContainerUtils: ArticleContainerUtils,
    private _componentUtils: ContainerService,
    private connectionInfoService: ConnectionInfoService
  ) {}

  public refreshPinsVisibility(data: any): void {
    const articleContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(data.container.articleGUID);
    if (articleContainer && articleContainer.article && ArticleUtils.isBridge(articleContainer.article)) {
      const textureElements = ArticleUtils.getTextureElements(articleContainer.article);
      for (let i = 0; i < textureElements.length; i++) {
        const connectionMetaElement: MetaElementVO = this.findReferenceMetaElement(textureElements[i], articleContainer);
        if (connectionMetaElement) {
          const connectionUsage: MetaElementUsageVO = articleContainer.findMetaElementUsageByName(connectionMetaElement.name);
          if (!connectionUsage) {
            articleContainer.hideConnection(textureElements[i].name);
          }
        }
      }
    }
  }

  public breakPins(connectionCubes: Connection[]): void {
    for (const cube of connectionCubes) {
      this.breakPinConnection(cube);
    }
  }

  private breakPinConnection(connectionCube: Connection): boolean {
    const articleContainer: ArticleContainer = this._componentUtils.getArticleContainer(connectionCube);
    if (!articleContainer) {
      return false;
    }
    const connectionArea: MetaElementVO = connectionCube.metaElementVO;
    if (!connectionArea) {
      return false;
    }
    articleContainer.detachMetaElementUsageByName(connectionArea.name);
    const textureMetaElement: MetaElementVO = this.findReferenceMetaElement(connectionArea, articleContainer);
    if (textureMetaElement) {
      articleContainer.hideConnection(textureMetaElement.name);
    }
    return true;
  }

  private findReferenceMetaElement(metaElement: MetaElementVO, articleContainer: ArticleContainer): MetaElementVO {
    if (!articleContainer || !metaElement) {
      return null;
    }
    const referenceConnectionName: string = PropertyUtils.getStringPropertyValue(
      metaElement.behaviorProperties,
      MetaElementBehaviorPropertyDef.DYNAMIC_PIN_REFERENCE
    );

    return !referenceConnectionName ? null : articleContainer.findMetaElementByName(referenceConnectionName);
  }

  public recoverPins(connectionCubes: Connection[]): void {
    for (const cube of connectionCubes) {
      this.recoverPinConnection(cube);
    }
  }

  private recoverPinConnection(connectionCube: Connection): boolean {
    const articleContainer: ArticleContainer = this._componentUtils.getArticleContainer(connectionCube);
    if (!articleContainer) {
      return false;
    }
    const connectionArea: MetaElementVO = connectionCube.metaElementVO;
    if (!connectionArea) {
      return false;
    }
    const requiredConnection: Connection = this.connectionInfoService.findRequiredConnectionInGlobal(connectionCube, [articleContainer.articleGUID]);
    if (!requiredConnection) {
      return false;
    }
    let connectionUsageAttached: boolean = true;
    if (ArticleUtils.isStaggeredJumperArticle(articleContainer.article)) {
      connectionUsageAttached = this._articleContainerUtils.hasConnectedStaggeredJumper(requiredConnection) ? false : true;
    }
    if (connectionUsageAttached) {
      articleContainer.attachMetaElementUsageByName(connectionArea.name, requiredConnection);
      const textureMetaElement: MetaElementVO = this.findReferenceMetaElement(connectionArea, articleContainer);
      if (textureMetaElement) {
        articleContainer.showConnection(textureMetaElement.name);
        const textureCube: Connection = articleContainer.findFirstConnectionByName(textureMetaElement.name);
        if (textureCube) {
          textureCube.refresh();
        }
      }
    }
    return connectionUsageAttached;
  }

  /**
   * @return broken pin-connections
   */
  public breakPinsByTextureConnections(textureConnections: Connection[]): Connection[] {
    const brokenConnections: Connection[] = [];
    for (const connection of textureConnections) {
      const brokenConnection: Connection = this.breakPinByTextureConnection(connection);
      if (brokenConnection) {
        brokenConnections.push(brokenConnection);
      }
    }
    this.sceneManager.renderScene();
    return brokenConnections;
  }

  /**
   * @return recovered pin-connections
   */
  public recoverPinsInArticleContainers(articleContainers: ArticleContainer[]): Connection[] {
    let recoveredConnections: Connection[] = [];
    for (const container of articleContainers) {
      recoveredConnections = recoveredConnections.concat(this.recoverArticlePins(container));
    }
    this.sceneManager.renderScene();
    return recoveredConnections;
  }

  /**
   * @return broken connection cube, or null if pin wasn't broken
   */
  private breakPinByTextureConnection(textureConnection: Connection): Connection {
    const articleContainer: ArticleContainer = this._componentUtils.getArticleContainer(textureConnection);
    if (articleContainer != null) {
      const connectionMetaElement: MetaElementVO = this.findReferenceMetaElement(textureConnection.metaElementVO, articleContainer);
      if (connectionMetaElement) {
        articleContainer.detachMetaElementUsageByName(connectionMetaElement.name);
        articleContainer.hideConnection(textureConnection.connectionName);
        return articleContainer.findFirstConnectionByName(connectionMetaElement.name);
      }
    }
    return null;
  }

  private recoverArticlePins(articleContainer: ArticleContainer): Connection[] {
    const recoveredPinConnections: Connection[] = [];
    const givingConnectionAreas: MetaElementVO[] = ConnectionInfoUtils.getFreeConnectionAreas(articleContainer, ConnectionAreaVO.SIDE_GIVING);
    for (const connectionArea of givingConnectionAreas) {
      const pinConnection: Connection = articleContainer.findFirstConnectionByName(connectionArea.name);
      const pinRecovered: boolean = this.recoverPinConnection(pinConnection);
      if (pinRecovered) {
        recoveredPinConnections.push(pinConnection);
      }
    }
    return recoveredPinConnections;
  }
}
